import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getServiceDimensionValues } from "slices/dimensions";
import { formFieldDimensionKey as dimensionKeyOf, hasServiceLookup } from "common/formFieldLookup";
import StateService from "../../services/state.service";


// Stable reference: `|| []` builds a new array per call, defeating
// react-redux's reference check and re-rendering on every dispatched action.
const EMPTY_DIMENSIONS = [];

/**
 * NiceFormDisplay Component
 *
 * Displays form type field data inline in the table with merged rows.
 * Used as a render type for displaying form field values in the data grid.
 */
const NiceFormDisplay = ({
  value,
  formReference,
  aliasName,
  // Parent field's service configuration for default lookup
  serviceType,
  serviceImplemenator,
  serviceDimension,
  lookUpLabel,
  serviceFilter,
}) => {
  const dispatch = useDispatch();
  const hasValue = Array.isArray(value) ? value.length > 0 : Boolean(value);
  const needsSchema = hasValue && Boolean(formReference);
  // Start in the loading state when a schema is needed, so the first paint is the
  // spinner rather than a flash of raw ids.
  const [isLoadingSchema, setIsLoadingSchema] = useState(needsSchema);
  const [referencedSchema, setReferencedSchema] = useState(null);

  // Get all dimensions from Redux
  const dimensions = useSelector((state) => state.dimensions?.dimensions ?? EMPTY_DIMENSIONS);

  // Fetch the referenced state set UI schema. Every row of the column asks for the same
  // one, so go through the shared request rather than one call per cell.
  useEffect(() => {
    if (!needsSchema) {
      setIsLoadingSchema(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoadingSchema(true);

    StateService.getStateUiShared(formReference)
      .then((response) => {
        if (!cancelled) setReferencedSchema(response);
      })
      .catch((error) => {
        console.error("Error fetching referenced state set schema:", error);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSchema(false);
      });

    return () => {
      cancelled = true;
    };
  }, [formReference, needsSchema]);

  // Get field schemas - include default field from parent's serviceType if configured
  const fieldSchemas = React.useMemo(() => {
    if (!referencedSchema?.columns) return [];

    const allFields = [];

    // If parent field has serviceType configured, add it as a default lookup field
    if (serviceType && serviceImplemenator && serviceDimension) {
      const defaultField = {
        field: serviceDimension, // Use serviceDimension as field name (e.g., "_id")
        headerName: aliasName || formReference, // Use aliasName if set, otherwise formReference
        uiType: "string",
        bsonType: "string",
        serviceType: serviceType,
        serviceImplemenator: serviceImplemenator,
        serviceDimension: serviceDimension,
        lookUpLabel: lookUpLabel,
        serviceFilter: serviceFilter,
        required: false,
        isArray: false,
        sequence: -1, // Put it first
      };
      allFields.push(defaultField);
    }

    // Add all the schema columns
    allFields.push(...referencedSchema.columns);

    return allFields;
  }, [referencedSchema, serviceType, serviceImplemenator, serviceDimension, lookUpLabel, serviceFilter, formReference, aliasName]);

  const lookupFields = React.useMemo(() => fieldSchemas.filter(hasServiceLookup), [fieldSchemas]);

  // Request each lookup. Every row of the column dispatches the same keys; `ifMissing`
  // makes the thunk skip any key already in the store, so that costs one request each.
  useEffect(() => {
    lookupFields.forEach((item) => {
      const search = {
        type: item.serviceType,
        implementor: item.serviceImplemenator,
        field: item.serviceDimension,
        lookUpLabel: item.lookUpLabel,
        filter: item.serviceFilter,
        dimensionKey: dimensionKeyOf(item),
      };

      dispatch(getServiceDimensionValues({ search, ifMissing: true }));
    });
  }, [lookupFields, dispatch]);

  // Derived from the store rather than kept as local state: loading while any lookup has
  // no entry yet or is still in flight. Failed and empty lookups settle with
  // loading=false, so the cell always leaves this state once its requests finish.
  const isLoadingDimensions = lookupFields.some((item) => {
    const entry = dimensions.find((d) => d.dimensionKey === dimensionKeyOf(item));
    return !entry || entry.loading;
  });

  // Handle both single object and array of objects
  const dataArray = Array.isArray(value) ? value : value ? [value] : [];

  // Get all unique keys from all entries for table columns
  const allKeys = new Set();
  dataArray.forEach((entry) => {
    if (entry && typeof entry === "object") {
      Object.keys(entry).forEach((key) => allKeys.add(key));
    }
  });
  const columns = Array.from(allKeys);

  // Get display value for a field (label instead of value ID)
  const getDisplayValue = (fieldName, fieldValue) => {
    if (fieldValue === undefined || fieldValue === null) {
      return "-";
    }

    // Find the field schema
    const fieldSchema = fieldSchemas.find((f) => f.field === fieldName);

    if (!fieldSchema) {
      return String(fieldValue);
    }

    // If it's a dropdown field, try to get the label
    if (hasServiceLookup(fieldSchema)) {
      const dimensionKey = dimensionKeyOf(fieldSchema);
      const dimensionData = dimensions.find((d) => d.dimensionKey === dimensionKey);

      if (dimensionData?.values) {
        const option = dimensionData.values.find((v) => v.value === fieldValue);
        if (option) {
          // If label is a template placeholder (e.g., "<group_name>"), show the value (_id) instead
          const label = option.label;
          if (label && label.startsWith("<") && label.endsWith(">")) {
            return option.value;
          }
          return label || option.value;
        }
      }
    }

    return String(fieldValue);
  };

  // Get header names for columns
  const getHeaderName = (fieldName) => {
    if (aliasName && columns.length === 1) return aliasName;
    const fieldSchema = fieldSchemas.find((f) => f.field === fieldName);
    return fieldSchema?.headerName || fieldSchema?.uiHeader || fieldName;
  };

  // Early return after all hooks
  if (!hasValue) {
    return <Typography color="text.secondary">-</Typography>;
  }

  // Show loading state
  if (isLoadingSchema || isLoadingDimensions) {
    return (
      <Box display="flex" alignItems="center">
        <CircularProgress size={16} />
        <Typography ml={1} variant="body2">Loading...</Typography>
      </Box>
    );
  }

  // Generate text representation for CSV export
  const getTextRepresentation = () => {
    return dataArray.map((row) => {
      const rowData = columns.map((col) => {
        const displayValue = getDisplayValue(col, row[col]);
        return `${getHeaderName(col)}: ${displayValue}`;
      }).join('; ');
      return rowData;
    }).join(' | ');
  };

  // Render inline table with column headers that blends with parent table
  return (
    <Box
      data-export-value={getTextRepresentation()}
      sx={{ width: '100%' }}
    >
      <Table
        size="small"
        sx={{
          width: '100%',
          '& .MuiTableCell-root': {
            padding: '8px 16px',
            fontSize: '0.875rem',
            border: '1px solid rgba(224, 224, 224, 1)'
          }
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col}
                sx={{
                  fontWeight: 600,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }}
              >
                {getHeaderName(col)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {dataArray.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {columns.map((col) => (
                <TableCell key={col}>
                  {getDisplayValue(col, row[col])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default React.memo(NiceFormDisplay);
